/**
 * DocRow — one document row in the HubList.
 *
 * Compact horizontal layout (launch-film screen 2 "SOURCES" list):
 *   [PDF/DOC/XLS icon] filename ............ [✓ indexed | status pill]
 *                      ConfPill · doctype-badge · [blocking?] · signals
 *
 * The DocumentRow data model (from M2) carries everything the row
 * needs — strategy doc_type + Spec 32 source_doctype + per-tag
 * confidences + verified flag + blocking flag + low flag.
 *
 * Click → fires onClick(doc); HubDetail (M6) is the eventual
 * destination. The row itself is purely presentational; the
 * parent owns selection state.
 */

import { Button, makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import { ArrowDownload24Regular, ArrowUpload24Regular, Delete24Regular } from "@fluentui/react-icons";
import type React from "react";
import { useRef } from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import { isDerivedFile } from "../mergeUploadsAndInventory";
import { couldNotReadCause, rowStatusWord } from "../statusWords";
import type { DocumentRow } from "../types";

export interface DocRowProps {
  row: DocumentRow;
  onClick?: (row: DocumentRow) => void;
  onDeleteDoc?: (row: DocumentRow) => void;
  onDownloadDoc?: (row: DocumentRow) => void;
  /** Upload an updated version of this document under the same name. */
  onReplaceDoc?: (row: DocumentRow, file: File) => void;
  /** An updated version of this document is uploading/processing: show a
   *  spinner in place of its status and hold its replace/delete actions. */
  replacing?: boolean;
}

// DECISION: a deeper green than statusColors.cool for the "✓ indexed"
// text — cool (#26DF9B) is a fill colour and fails contrast as 10px text.
const INDEXED_GREEN = "#0E9F6E";

const useStyles = makeStyles({
  rowButton: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${surfaceColors.line}`,
    cursor: "pointer",
    fontFamily: fonts.body,
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  inner: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    padding: "10px 14px",
  },
  // Small file-type icon (film: red "PDF" page). Colour = file kind.
  fileBadge: {
    width: "20px",
    height: "24px",
    borderRadius: "3px",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "3px",
    boxSizing: "border-box",
    fontSize: "6.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    letterSpacing: "0.2px",
    flexShrink: 0,
  },
  fileBadgePdf: {
    backgroundColor: "var(--danger-soft)",
    color: statusColors.danger,
  },
  fileBadgeWord: {
    backgroundColor: "var(--info-soft)",
    color: "var(--info)",
  },
  fileBadgeSheet: {
    backgroundColor: "var(--success-soft)",
    color: "var(--success)",
  },
  fileBadgeOther: {
    backgroundColor: surfaceColors.paperDim,
    color: inkColors.ink4,
  },
  // Right-aligned lifecycle status (film: green mono "✓ indexed").
  status: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    flexShrink: 0,
    padding: "1px 6px",
    borderRadius: "9999px",
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.mono,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
    lineHeight: 1.5,
  },
  statusIndexed: {
    // Plain green text — no fill, like the film.
    color: INDEXED_GREEN,
    paddingLeft: 0,
    paddingRight: 0,
  },
  // No filled badges: Reading in grey, problems in red text.
  statusWorking: {
    color: inkColors.ink3,
    paddingLeft: 0,
    paddingRight: 0,
  },
  statusFailed: {
    color: statusColors.danger,
    paddingLeft: 0,
    paddingRight: 0,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    fontSize: "12.5px",
    fontWeight: 500,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: "3px",
  },
  metaRow: {
    ":empty": {
      display: "none",
    },
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
    rowGap: "3px",
    flexWrap: "wrap",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    border: "1px solid transparent",
  },
  pillVerified: {
    backgroundColor: "var(--success-soft)",
    border: `1px solid ${statusColors.cool}`,
    color: "var(--success)",
  },
  pillHigh: {
    backgroundColor: "var(--success-soft)",
    border: `1px solid ${statusColors.cool}`,
    color: "var(--success)",
  },
  pillMid: {
    backgroundColor: "var(--warning-soft)",
    border: `1px solid ${statusColors.warn}`,
    color: "var(--warning)",
  },
  pillLow: {
    backgroundColor: "var(--danger-soft)",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
  },
  doctypeBadge: {
    fontSize: "9.5px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: brandColors.light,
    color: brandColors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  blockingBadge: {
    fontSize: "9.5px",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: "var(--danger-soft)",
    color: statusColors.danger,
    border: `1px solid ${statusColors.danger}`,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  sizeText: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
  },
  paramRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "3px",
    marginTop: "4px",
  },
  paramChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink3,
  },
  paramChipKey: {
    color: inkColors.ink4,
  },
  paramChipValue: {
    color: inkColors.ink,
    fontWeight: 600,
  },
  chevron: {
    marginTop: "4px",
    flexShrink: 0,
    color: inkColors.ink4,
  },
  deleteButton: {
    marginTop: "4px",
    marginLeft: "6px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: statusColors.danger,
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    ":hover": {
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  downloadButton: {
    marginTop: "4px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: inkColors.ink4,
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    ":hover": {
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  // ── Phase 2: AI intelligence additions ──────────────────────────
  filenameRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    minHeight: "24px",
  },
  confidenceBarWrap: {
    marginTop: "4px",
  },
});

type FileKind = "pdf" | "word" | "sheet" | "other";

function fileKind(filename: string): FileKind {
  const ext = filename.slice(filename.lastIndexOf(".") + 1).toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx" || ext === "rtf") return "word";
  if (ext === "xls" || ext === "xlsx" || ext === "csv") return "sheet";
  return "other";
}

function fileExtensionShort(filename: string): string {
  const idx = filename.lastIndexOf(".");
  if (idx === -1 || idx === filename.length - 1) return "FILE";
  return filename.slice(idx + 1, idx + 4).toUpperCase();
}

/**
 * Right-aligned status, in the library's one set of words (statusWords.ts):
 * Ready, Uploading, Waiting to read, Reading, Didn't upload, Couldn't read.
 * A problem carries its plain cause as the tooltip, never the raw string.
 */
function RowStatus(props: { row: DocumentRow }): React.ReactElement {
  const styles = useStyles();
  const { row } = props;
  const word = rowStatusWord(row.processing_status, row.upstream_status);
  switch (row.processing_status) {
    case "ready":
    case "limited":
      return (
        <span className={mergeClasses(styles.status, styles.statusIndexed)} data-testid="docrow-status">
          ✓ {word}
        </span>
      );
    case "uploading": {
      const pct = row.upload_progress_percent;
      return (
        <span className={mergeClasses(styles.status, styles.statusWorking)} data-testid="docrow-status">
          {typeof pct === "number" ? `${word} ${Math.round(pct)}%` : word}
        </span>
      );
    }
    case "indexing":
    case "classifying":
      return (
        <span className={mergeClasses(styles.status, styles.statusWorking)} data-testid="docrow-status">
          {word}
        </span>
      );
    case "failed_upload":
      return (
        <span
          className={mergeClasses(styles.status, styles.statusFailed)}
          title="Try uploading it again"
          data-testid="docrow-status"
        >
          {word}
        </span>
      );
    case "failed_processing":
      return (
        <span
          className={mergeClasses(styles.status, styles.statusFailed)}
          title={couldNotReadCause(row.upstream_status)}
          data-testid="docrow-status"
        >
          {word}
        </span>
      );
  }
}

/** The document type when Asthra knows it; nothing when it doesn't. */
function docTypeLabel(row: DocumentRow): string | null {
  return row.source_classification?.source_doctype || row.doc_type || null;
}

export const DocRow: React.FC<DocRowProps> = ({
  row,
  onClick,
  onDeleteDoc,
  onDownloadDoc,
  onReplaceDoc,
  replacing = false,
}) => {
  const styles = useStyles();
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const handleClick = (): void => onClick?.(row);
  // Keys on the delete/download buttons inside reach here too; only the row's own.
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDeleteDoc?.(row);
  };
  const handleDownload = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDownloadDoc?.(row);
  };
  const handleReplaceClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    replaceInputRef.current?.click();
  };
  const handleReplaceFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onReplaceDoc?.(row, file);
  };
  // Only a document that is in the library can be replaced.
  const canReplace = Boolean(onReplaceDoc && row.doc_id && row.processing_status === "ready" && !replacing);
  // ``doc_id`` is null only while an upload's POST is still in flight — there's
  // no stored file to pull yet, so hide the download affordance until it lands.
  const canDownload = Boolean(onDownloadDoc && row.doc_id);
  const kind = fileKind(row.filename);
  return (
    // A plain <div role="button"> rather than a real <button> — the
    // delete affordance below is itself a <button>, and nesting
    // interactive controls (<button> inside <button>) is invalid HTML
    // that some WebViews (this ships inside an Office Add-in task
    // pane) dispatch inconsistently, occasionally double-firing the
    // inner control's click.
    // biome-ignore lint/a11y/useSemanticElements: a <button> here would nest the delete/download buttons
    <div
      role="button"
      tabIndex={0}
      className={styles.rowButton}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={`doc-row-${row.doc_id ?? row.filename}`}
      aria-label={`Document ${row.filename}`}
    >
      <div className={styles.inner}>
        <div
          className={mergeClasses(
            styles.fileBadge,
            kind === "pdf" && styles.fileBadgePdf,
            kind === "word" && styles.fileBadgeWord,
            kind === "sheet" && styles.fileBadgeSheet,
            kind === "other" && styles.fileBadgeOther,
          )}
          aria-hidden="true"
          data-testid="docrow-type"
        >
          {fileExtensionShort(row.filename)}
        </div>
        <div className={styles.rowMain}>
          <div className={styles.filenameRow}>
            <span className={styles.filename} style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
              {row.filename}
            </span>
            {replacing ? (
              <Spinner
                size="extra-tiny"
                aria-label={`Updating ${row.filename}`}
                title="Updating to the new version; the old one stays until it is processed"
                data-testid="doc-row-replacing"
              />
            ) : (
              <RowStatus row={row} />
            )}
          </div>
          {(isDerivedFile(row.filename) || docTypeLabel(row)) && (
            <div className={styles.metaRow}>
              {isDerivedFile(row.filename) ? (
                <span
                  className={styles.doctypeBadge}
                  title="A table built during a run from your documents; its numbers cite those sources"
                  data-testid="doc-row-derived"
                >
                  Generated
                </span>
              ) : (
                <span className={styles.doctypeBadge}>{docTypeLabel(row)}</span>
              )}
            </div>
          )}
        </div>
        {canDownload && (
          <button
            type="button"
            className={styles.downloadButton}
            onClick={handleDownload}
            aria-label={`Download document ${row.filename}`}
            title="Download this document"
          >
            <ArrowDownload24Regular aria-hidden="true" className={"w-5 h-5"} />
          </button>
        )}
        {canReplace && (
          <>
            <button
              type="button"
              className={styles.downloadButton}
              onClick={handleReplaceClick}
              aria-label={`Upload updated version of ${row.filename}`}
              title="Upload updated version…"
              data-testid="doc-row-replace"
            >
              <ArrowUpload24Regular aria-hidden="true" className={"w-5 h-5"} />
            </button>
            <input
              ref={replaceInputRef}
              type="file"
              hidden
              onChange={handleReplaceFile}
              onClick={(e) => e.stopPropagation()}
              data-testid="doc-row-replace-input"
            />
          </>
        )}
        {onDeleteDoc && !replacing && (
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDelete}
            aria-label={`Delete document ${row.filename}`}
            title="Delete this document"
          >
            <Delete24Regular aria-hidden="true" className={"w-5 h-5"} />
          </button>
        )}
      </div>
    </div>
  );
};

export default DocRow;
